import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { UserService } from '../../layout/service/user.service';

@Component({
    selector: 'app-edit-video-material',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        TextareaModule,
        SelectModule,
        CardModule,
        DividerModule,
        ToastModule,
        ToggleSwitchModule,
        FileUploadModule,
        InputNumberModule
    ],
    providers: [MessageService],
    template: `
        <div class="min-h-screen bg-gray-50 p-4">
            <div class="max-w-4xl mx-auto">
                <div class="bg-white rounded-lg shadow-sm border border-gray-200">
                    <!-- Header -->
                    <div class="flex justify-between items-center p-6 border-b border-gray-200">
                        <div>
                            <h2 class="text-2xl font-bold text-gray-900 flex items-center">
                                <i class="pi pi-pencil mr-2 text-blue-600"></i>
                                Edit Video Material
                            </h2>
                            <p class="text-gray-600 mt-2">Edit konten video untuk pembelajaran OSCE</p>
                        </div>
                        <p-button
                            (onClick)="clearAll()"
                            severity="danger"
                            icon="pi pi-trash"
                            label="Reset"
                            [outlined]="true"
                        ></p-button>
                    </div>

                    <!-- Video Preview -->
                    <div class="mb-6" *ngIf="videoData?.path">
                        <h3 class="text-lg font-semibold text-gray-900 mb-4 pl-4 pt-2 flex items-center">
                            <i class="pi pi-play-circle mr-2 text-blue-600"></i>
                            Preview Video Saat Ini
                        </h3>
                        <div class="bg-gray-100 rounded-lg p-4">
                            <video 
                                controls 
                                controlsList="nodownload"
                                disablePictureInPicture
                                class="w-full max-h-96 rounded-lg shadow-sm"
                                [src]="videoData.path"
                                preload="metadata">
                                Browser Anda tidak mendukung pemutaran video.
                            </video>
                            <div class="mt-2 text-sm text-gray-600">
                                <strong>Durasi:</strong> {{formatDuration(videoData.duration)}} |
                                <strong>Kategori:</strong> {{videoData.kategori}} |
                                <strong>Level:</strong> {{videoData.level || 'Tidak ditentukan'}}
                            </div>
                        </div>
                    </div>
                    <div class="bg-white border border-gray-200 rounded-lg p-6">
                        <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <i class="pi pi-info-circle mr-2 text-blue-600"></i>
                            Informasi Video Material
                        </h3>

                        <!-- Nama Video -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Nama Video <span class="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                [(ngModel)]="videoForm.name"
                                placeholder="Masukkan nama video..."
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        </div>

                        <!-- Deskripsi Video -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Deskripsi Video
                            </label>
                            <textarea
                                pInputTextarea
                                [(ngModel)]="videoForm.description"
                                placeholder="Masukkan deskripsi video..."
                                rows="4"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                            </textarea>
                        </div>

                        <!-- Upload Video Baru (Opsional) -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                File Video Baru (Opsional)
                            </label>
                            <p-fileUpload
                                mode="basic"
                                accept="video/*"
                                [maxFileSize]="104857600"
                                (onSelect)="onVideoSelect($event)"
                                [auto]="false"
                                chooseLabel="Pilih Video Baru"
                                class="w-full">
                            </p-fileUpload>
                            <small class="text-gray-500 mt-1 block">Maksimal 100MB, format: MP4, AVI, dll. Biarkan kosong jika tidak ingin mengubah video</small>
                            <div *ngIf="selectedVideo" class="mt-2 text-sm text-green-600">
                                File baru dipilih: {{ selectedVideo.name }}
                            </div>
                            <div *ngIf="!selectedVideo && currentVideo" class="mt-2 text-sm text-blue-600">
                                Video saat ini: {{ currentVideo }}
                            </div>
                        </div>

                        <!-- Upload Thumbnail Baru (Opsional) -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Thumbnail Baru (Opsional)
                            </label>
                            <p-fileUpload
                                mode="basic"
                                accept="image/*"
                                [maxFileSize]="5242880"
                                (onSelect)="onThumbnailSelect($event)"
                                [auto]="false"
                                chooseLabel="Pilih Thumbnail Baru"
                                class="w-full">
                            </p-fileUpload>
                            <small class="text-gray-500 mt-1 block">Maksimal 5MB, format: JPG, PNG, dll. Biarkan kosong jika tidak ingin mengubah thumbnail</small>
                            <div *ngIf="selectedThumbnail" class="mt-2 text-sm text-green-600">
                                File baru dipilih: {{ selectedThumbnail.name }}
                            </div>
                            <div *ngIf="!selectedThumbnail && currentThumbnail" class="mt-2 text-sm text-blue-600">
                                Thumbnail saat ini: {{ currentThumbnail }}
                            </div>
                        </div>

                        <!-- Kategori -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Kategori <span class="text-red-500">*</span>
                            </label>
                            <p-select
                                [(ngModel)]="videoForm.kategori"
                                [options]="categoryOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Pilih atau ketik kategori"
                                [editable]="true"
                                class="w-full">
                            </p-select>
                        </div>

                        <!-- Level -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Level <span class="text-red-500">*</span>
                            </label>
                            <p-select
                                [(ngModel)]="videoForm.level"
                                [options]="levelOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Pilih level"
                                class="w-full">
                            </p-select>
                        </div>

                        <!-- Durasi -->
                        <div class="mb-6">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                Durasi (detik) <span class="text-red-500">*</span>
                            </label>
                            <p-inputNumber
                                [(ngModel)]="videoForm.duration"
                                placeholder="Masukkan durasi dalam detik"
                                class="w-full"
                                [min]="1"
                                suffix=" detik">
                            </p-inputNumber>
                        </div>

                        <!-- Status Tampil -->
                        <div class="mb-6">
                            <div class="flex items-center justify-between">
                                <label class="block text-sm font-medium text-gray-700">
                                    Tampilkan Video
                                </label>
                                <div class="flex items-center space-x-3">
                                    <span class="text-sm text-gray-600">{{videoForm.is_show ? 'Ya' : 'Tidak'}}</span>
                                    <p-toggleswitch [(ngModel)]="videoForm.is_show"></p-toggleswitch>
                                </div>
                            </div>
                            <small class="text-gray-500 mt-1 block">Aktifkan untuk menampilkan video ini kepada pengguna</small>
                        </div>

                        <hr class="my-6 border-gray-200">

                        <!-- Actions -->
                        <div class="flex justify-end space-x-3">
                            <p-button
                                (onClick)="cancel()"
                                severity="secondary"
                                icon="pi pi-times"
                                label="Batal"
                                [outlined]="true"
                            ></p-button>
                            <p-button
                                (onClick)="save()"
                                severity="success"
                                icon="pi pi-save"
                                label="Update Video"
                                [loading]="saving"
                            ></p-button>
                        </div>
                    </div>

                    <!-- Loading State -->
                    <div *ngIf="loading" class="p-6 text-center">
                        <i class="pi pi-spin pi-spinner text-2xl text-blue-600"></i>
                        <p class="mt-2 text-gray-600">Memuat data...</p>
                    </div>
                </div>
            </div>
        </div>

        <p-toast></p-toast>
    `
})
export class EditVideoMaterial implements OnInit {
    videoId: number = 0;
    loading = false;
    saving = false;
    currentVideo: string = '';
    currentThumbnail: string = '';
    videoData: any = null;

    videoForm = {
        name: '',
        kategori: '',
        level: '',
        is_show: true,
        duration: null as number | null,
        description: ''
    };

    selectedVideo: File | null = null;
    selectedThumbnail: File | null = null;

    categoryOptions: { label: string; value: string }[] = [];

    levelOptions = [
        { label: 'UKNPDPD', value: 'UKNPDPD' },
        { label: 'Preklinik', value: 'Preklinik' },
    ];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private messageService: MessageService
    ) { }

    ngOnInit() {
        this.checkUserAuthorization();
        this.loadCategories();
        this.videoId = Number(this.route.snapshot.paramMap.get('id'));
        if (this.videoId) {
            this.loadVideoData();
        }
    }

    loadCategories() {
        this.userService.getAdminKategori().then(
            (response) => {
                if (response && response.data && Array.isArray(response.data)) {
                    const activeCategories = response.data
                        .filter((item: any) => String(item.IS_ACTIVED) === '1' && item.KATEGORI)
                        .map((item: any) => item.KATEGORI.trim())
                        .filter((cat: string) => cat.length > 0);
                    
                    const uniqueCategories = Array.from(new Set<string>(activeCategories)).sort();
                    this.categoryOptions = uniqueCategories.map(cat => ({ label: cat, value: cat }));
                    
                    // If video current category is not in list, add it
                    if (this.videoForm.kategori && !this.categoryOptions.some(opt => opt.value === this.videoForm.kategori)) {
                        this.categoryOptions.unshift({ label: this.videoForm.kategori, value: this.videoForm.kategori });
                    }
                }
            },
            (error) => {
                console.error('Error loading video categories:', error);
            }
        );
    }

    checkUserAuthorization() {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                if (user.ID_ACCESS_ROLE !== 'N2FWBVCZUE' && user.ID_ACCESS_ROLE !== '4AEJ1KAJTY') {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Akses Ditolak',
                        detail: 'Anda tidak memiliki izin untuk mengakses halaman ini'
                    });
                    this.router.navigate(['/app/video_material/list']);
                    return;
                }
            } else {
                this.router.navigate(['/auth/login']);
                return;
            }
        } catch (error) {
            console.error('Error checking user authorization:', error);
            this.router.navigate(['/auth/login']);
        }
    }

    loadVideoData() {
        this.loading = true;
        this.userService.getVideoMaterial(this.videoId).then(
            (response) => {
                const video = response.data || response;
                this.videoData = video; // Store full video data for preview
                this.videoForm = {
                    name: video.name || '',
                    kategori: video.kategori || '',
                    level: video.level || '',
                    is_show: video.is_show == '1',
                    duration: video.duration || null,
                    description: video.description || ''
                };
                this.currentVideo = video.video_filename || video.video || 'Video tersimpan';
                this.currentThumbnail = video.thumbnail_filename || video.thumbnail || 'Tidak ada thumbnail';
                this.loading = false;
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat data video material'
                });
                this.loading = false;
            }
        );
    }

    onVideoSelect(event: any) {
        this.selectedVideo = event.files[0];

        // Auto-fill duration from video file
        if (this.selectedVideo) {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                this.videoForm.duration = Math.floor(video.duration);
                // Force change detection
                this.videoForm = { ...this.videoForm };
            };

            video.src = URL.createObjectURL(this.selectedVideo);
        }
    }

    onThumbnailSelect(event: any) {
        this.selectedThumbnail = event.files[0];
    }

    save() {
        if (!this.videoForm.name || !this.videoForm.kategori || !this.videoForm.level || !this.videoForm.duration) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Harap lengkapi semua field yang wajib diisi'
            });
            return;
        }

        this.saving = true;

        // Create FormData for file upload
        const formData = new FormData();
        if (this.selectedVideo) {
            formData.append('video', this.selectedVideo);
        }
        if (this.selectedThumbnail) {
            formData.append('thumbnail', this.selectedThumbnail);
        }
        formData.append('name', this.videoForm.name);
        formData.append('kategori', this.videoForm.kategori);
        formData.append('level', this.videoForm.level);
        formData.append('is_show', this.videoForm.is_show ? '1' : '0');
        formData.append('duration', this.videoForm.duration.toString());
        formData.append('description', this.videoForm.description);
        formData.append('id', this.videoId.toString());

        // Call API
        this.userService.updateVideoMaterial(formData).then(
            (response) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Berhasil',
                    detail: 'Video material berhasil diupdate'
                });
                this.saving = false;
                this.router.navigate(['/app/video_material']);
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal mengupdate video material: ' + (error.error?.message || error.message)
                });
                this.saving = false;
            }
        );
    }

    cancel() {
        this.router.navigate(['/app/video_material']);
    }

    clearAll() {
        this.videoForm = {
            name: '',
            kategori: '',
            level: '',
            is_show: true,
            duration: null,
            description: ''
        };
        this.selectedVideo = null;
        this.selectedThumbnail = null;
    }

    formatDuration(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
}